'use client';

import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { AppLink } from '@/components/mui/app-link';

import { findScrollableAncestor, getActiveSectionId } from './tool-section-navigation';

export type ToolSectionNavigationItem = {
  id: string;
  title: string;
};

type ToolSectionNavigationProps = {
  items: ToolSectionNavigationItem[];
  ariaLabel?: string;
  /**
   * The central tools page scrolls inside its content area, while some
   * consumers sit below the app-wide sticky header. Let those consumers
   * reserve the header's space without changing the central page's offset.
   */
  stickyTop?: { xs: number; md: number };
};

export function ToolSectionNavigation({
  items,
  ariaLabel = 'Seções de ferramentas',
  stickyTop,
}: ToolSectionNavigationProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  const animationFrameRef = useRef<number | null>(null);
  const navigationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (items.length === 0) return;

    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => Boolean(section));

    if (sections.length === 0) return;

    const scrollContainer = findScrollableAncestor(navigationRef.current);
    const scrollTarget = scrollContainer ?? window;

    const updateActiveSection = () => {
      animationFrameRef.current = null;

      const nextActiveId = getActiveSectionId(
        sections.map((section) => ({
          id: section.id,
          top: section.getBoundingClientRect().top,
        })),
      );

      setActiveId((currentActiveId) => (currentActiveId === nextActiveId ? currentActiveId : nextActiveId));
    };

    const scheduleActiveSectionUpdate = () => {
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    scrollTarget.addEventListener('scroll', scheduleActiveSectionUpdate, {
      passive: true,
    });
    window.addEventListener('resize', scheduleActiveSectionUpdate);

    return () => {
      scrollTarget.removeEventListener('scroll', scheduleActiveSectionUpdate);
      window.removeEventListener('resize', scheduleActiveSectionUpdate);
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [items]);

  const navigateToSection = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    window.history.replaceState(null, '', `#${id}`);
    setActiveId(id);
  };

  return (
    <Box
      component='nav'
      ref={navigationRef}
      aria-label={ariaLabel}
      sx={{
        position: 'sticky',
        top: stickyTop ?? { xs: 8, md: 20 },
        zIndex: 2,
        p: { xs: 0.75, md: 1.25 },
        border: '1px solid hsl(var(--border))',
        borderRadius: { xs: 2, md: 2.5 },
        backgroundColor: 'hsl(var(--card) / .96)',
        boxShadow: '0 12px 26px hsl(var(--foreground) / .08)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Typography
        variant='overline'
        color='text.secondary'
        sx={{
          display: { xs: 'none', md: 'block' },
          px: 1,
          mb: 0.75,
          fontWeight: 900,
          letterSpacing: 1.1,
        }}
      >
        Nesta página
      </Typography>
      <Stack
        direction={{ xs: 'row', md: 'column' }}
        spacing={0.5}
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: { xs: 'auto', md: 'visible' },
          scrollbarWidth: 'thin',
          pb: { xs: 0.25, md: 0 },
        }}
      >
        {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <AppLink
              key={item.id}
              href={`#${item.id}`}
              underline='none'
              aria-current={isActive ? 'location' : undefined}
              onClick={(event) => {
                event.preventDefault();
                navigateToSection(item.id);
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                minWidth: { xs: 'max-content', md: 0 },
                flexShrink: 0,
                minHeight: { xs: 34, md: 42 },
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'flex-start' },
                px: { xs: 1.25, md: 1.25 },
                borderRadius: 1.5,
                color: isActive ? 'primary.main' : 'text.secondary',
                backgroundColor: isActive ? 'hsl(var(--primary) / .1)' : 'transparent',
                fontSize: { xs: 11.5, md: 13 },
                fontWeight: isActive ? 900 : 750,
                textAlign: 'left',
                whiteSpace: 'nowrap',
                transition: 'color 160ms ease, background-color 160ms ease',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: { xs: 6, md: 0 },
                  top: { xs: 'auto', md: 8 },
                  bottom: { xs: 3, md: 8 },
                  width: { xs: 'calc(100% - 12px)', md: 3 },
                  borderRadius: 99,
                  backgroundColor: isActive ? 'hsl(var(--primary))' : 'transparent',
                },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'hsl(var(--primary) / .08)',
                },
                '&:focus-visible': {
                  outline: '2px solid hsl(var(--ring))',
                  outlineOffset: 2,
                },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                },
              }}
            >
              {item.title}
            </AppLink>
          );
        })}
      </Stack>
    </Box>
  );
}
